import { R_ENTRY, R_PLANET, R_SPAWN, TAU } from '../constants';
import { angleDelta, clamp, inArc, norm } from '../polar';
import { acquireDrone, acquireEffect, acquireProjectile, pushEvent } from '../world';
import type { Content, World } from '../types';
import { arcDamage, damageEnemy } from './damage';

/**
 * Ultimates share one rule: long cooldown, no cost (§5). They are the answer
 * when the angular read was wrong, not part of a rotation — so each one is
 * tuned to *rescue a bearing*, and each planet rescues it differently. That
 * difference is most of what makes the planets feel like separate characters.
 */
export function canFireUltimate(world: World): boolean {
  return world.ultCd <= 0 && (world.waveState === 'active' || world.waveState === 'clearing');
}

export function fireUltimate(world: World, content: Content, bearing: number): boolean {
  if (!canFireUltimate(world)) {
    pushEvent(world, { type: 'denied', id: 'ultimate' });
    return false;
  }

  const ult = world.ultimate;
  world.ultCd = ult.cooldown;
  world.stats.ultsFired++;
  pushEvent(world, { type: 'ultimate', theta: bearing, id: ult.id });

  switch (ult.kind) {
    case 'pulse': {
      // `count` > 1 sends a second front out behind the first (Second Dawn).
      const fronts = Math.max(1, ult.count);
      const travel = (R_SPAWN + 60 - R_PLANET) / Math.max(ult.frontSpeed, 1);
      for (let i = 0; i < fronts; i++) {
        const fx = acquireEffect(world, 'pulse');
        if (!fx) break;
        fx.theta = 0;
        fx.r = 0;
        fx.front = R_PLANET;
        fx.spread = TAU;
        fx.damage = ult.damage;
        // A negative start time delays the front without a separate scheduler.
        fx.t = -i * 1.8;
        fx.duration = travel;
        fx.dps = ult.duration; // reused: seconds of stun applied by the front
      }
      break;
    }

    case 'cone': {
      // `count` > 1 fans the cones evenly — that's how Twin Vent works.
      const cones = Math.max(1, ult.count);
      for (let i = 0; i < cones; i++) {
        const theta = norm(bearing + (i / cones) * TAU);
        const hit = new Set<number>();
        arcDamage(world, content, theta, ult.spread, R_PLANET, R_ENTRY, ult.damage, hit);

        if (ult.knockback > 0) {
          for (const e of world.enemies) {
            if (!e.alive || !hit.has(e.id)) continue;
            if (e.phase !== 'attack') continue;
            e.r = clamp(e.r + ult.knockback, R_PLANET, R_SPAWN);
          }
        }

        const fx = acquireEffect(world, 'cone');
        if (fx) {
          fx.theta = theta;
          fx.r = R_PLANET;
          fx.front = R_PLANET;
          fx.spread = ult.spread;
          fx.damage = ult.damage;
          fx.duration = 0.5;
          fx.dps = 0;
        }

        if (ult.groundDuration > 0) {
          const ground = acquireEffect(world, 'ground');
          if (ground) {
            ground.theta = theta;
            ground.r = R_PLANET;
            ground.front = R_SPAWN;
            ground.spread = ult.spread;
            ground.damage = 0;
            ground.dps = ult.groundDps;
            ground.duration = ult.groundDuration;
          }
        }
      }
      break;
    }

    case 'freeze': {
      for (const e of world.enemies) {
        if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
        e.freezeT = Math.max(e.freezeT, ult.duration);
      }
      // Damage lands after the freeze, so it benefits from the brittle bonus.
      arcDamage(world, content, 0, TAU, R_PLANET, R_ENTRY, ult.damage, new Set());
      const fx = acquireEffect(world, 'freeze');
      if (fx) {
        fx.theta = 0;
        fx.r = 0;
        fx.front = R_SPAWN + 40;
        fx.spread = TAU;
        fx.duration = ult.duration;
        fx.damage = 0;
        fx.dps = 0;
      }
      spawnGround(world, ult.groundDuration, ult.groundDps, 0, TAU);
      break;
    }

    case 'swarm': {
      for (let i = 0; i < ult.count; i++) {
        const d = acquireDrone(world);
        d.alive = true;
        d.theta = norm(bearing + (i / ult.count) * TAU);
        d.r = world.ringR + 34;
        // Alternate direction so the swarm sweeps rather than orbits in lockstep.
        d.vTheta = (i % 2 === 0 ? 1 : -1) * 0.55;
        d.life = ult.duration;
        d.cooldown = i * 0.05;
        d.damage = ult.damage;
      }
      break;
    }

    case 'singularity': {
      const fx = acquireEffect(world, 'singularity');
      if (fx) {
        fx.theta = bearing;
        fx.r = 0;
        fx.front = R_SPAWN;
        fx.spread = TAU;
        fx.duration = ult.duration;
        fx.damage = ult.damage;
        // Event Horizon crushes what it gathers; base Singularity just holds.
        fx.dps = ult.groundDps;
      }
      // Collapse: everything gets flung back out when the well lets go.
      if (ult.knockback > 0) {
        for (const e of world.enemies) {
          if (!e.alive || e.phase !== 'attack') continue;
          e.r = clamp(e.r + ult.knockback, R_PLANET, R_SPAWN);
        }
      }
      break;
    }

    case 'ringstorm': {
      const fx = acquireEffect(world, 'ringstorm');
      if (fx) {
        fx.theta = 0;
        fx.r = world.ringR;
        fx.front = 0;
        fx.spread = TAU;
        fx.duration = ult.duration;
        fx.damage = ult.damage;
        fx.dps = 0;
      }
      spawnGround(world, ult.groundDuration, ult.groundDps, 0, TAU);
      break;
    }
  }

  return true;
}

/** Lingering ground effect. Shared by every ultimate that leaves something behind. */
function spawnGround(
  world: World,
  duration: number,
  dps: number,
  theta: number,
  spread: number,
): void {
  if (duration <= 0 || dps <= 0) return;
  const ground = acquireEffect(world, 'ground');
  if (!ground) return;
  ground.theta = theta;
  ground.r = R_PLANET;
  ground.front = R_SPAWN;
  ground.spread = spread;
  ground.damage = 0;
  ground.dps = dps;
  ground.duration = duration;
}

/** Advance every live effect and apply whatever it does per tick. */
export function updateEffects(world: World, content: Content, dt: number): void {
  for (const fx of world.effects) {
    if (!fx.alive) continue;
    fx.t += dt;

    switch (fx.kind) {
      case 'pulse': {
        // A delayed front waits at the crust until its start time arrives.
        if (fx.t < 0) break;
        const prev = fx.front;
        fx.front += world.ultimate.frontSpeed * dt;
        for (const e of world.enemies) {
          if (!e.alive || fx.hit.has(e.id)) continue;
          if (e.phase === 'enter' || e.phase === 'dead') continue;
          if (e.r < prev - 10 || e.r > fx.front + 10) continue;
          fx.hit.add(e.id);
          e.stunT = Math.max(e.stunT, fx.dps);
          damageEnemy(world, content, e, fx.damage, e.theta);
        }
        break;
      }

      case 'ground': {
        // Burning ground: refreshes a burn on anything crossing the arc.
        for (const e of world.enemies) {
          if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
          if (e.r > fx.front) continue;
          if (fx.spread < TAU && !inArc(e.theta, fx.theta, fx.spread)) continue;
          e.burnT = Math.max(e.burnT, 0.6);
          e.burnDps = Math.max(e.burnDps, fx.dps);
        }
        break;
      }

      case 'singularity': {
        // Drag every bearing toward one. Repositions the battlefield itself.
        const pull = 2.1 * dt;
        for (const e of world.enemies) {
          if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
          const d = angleDelta(e.theta, fx.theta);
          const step = Math.min(Math.abs(d), pull) * Math.sign(d);
          e.theta = norm(e.theta + step);
          e.bearing = norm(e.bearing + step);
          if (fx.dps > 0) damageEnemy(world, content, e, fx.dps * dt, fx.theta);
        }
        break;
      }

      case 'ringstorm': {
        // Every ring slot firing outward, everywhere, for the duration.
        fx.front -= dt;
        if (fx.front <= 0) {
          fx.front = 0.07;
          const spokes = 14;
          const offset = fx.t * 1.7;
          for (let i = 0; i < spokes; i++) {
            const p = acquireProjectile(world);
            if (!p) break;
            p.alive = true;
            p.theta = norm(offset + (i / spokes) * TAU);
            p.r = world.ringR;
            p.target = -1;
            p.damage = fx.damage;
            p.splash = 14;
            p.life = 0.9;
            p.kind = 'bolt';
            p.vR = 520;
            p.vTheta = 0;
          }
        }
        break;
      }

      default:
        break;
    }

    if (fx.t >= fx.duration) fx.alive = false;
  }
}

/** Dumb-fire ring storm bolts still need to hurt what they pass through. */
export function updateStormHits(world: World, content: Content): void {
  for (const p of world.projectiles) {
    if (!p.alive || p.target !== -1) continue;
    for (const e of world.enemies) {
      if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
      if (Math.abs(e.r - p.r) > e.def.size * 0.5 + 10) continue;
      if (Math.abs(angleDelta(p.theta, e.theta)) > (e.def.size * 0.5 + 8) / Math.max(p.r, 1)) continue;
      damageEnemy(world, content, e, p.damage, p.theta);
      p.alive = false;
      break;
    }
  }
}
